// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import styles from '@app/features/channel/components/GifPicker.module.css';
import {safePause, safePlay, useGifVideoPool} from '@app/features/channel/components/GifVideoPool';
import {FavoriteGifFirstTimePromptModal} from '@app/features/channel/components/pickers/gif/FavoriteGifFirstTimePromptModal';
import {
	inferFormatContentType,
	pickBestPreviewFormat,
} from '@app/features/channel/components/pickers/gif/FavoriteGifTypes';
import type {GifPickerGridItemData} from '@app/features/channel/components/pickers/gif/GifPickerTypes';
import {usePooledVideo} from '@app/features/channel/components/pickers/shared/usePooledVideo';
import * as FavoriteGifCommands from '@app/features/expressions/commands/FavoriteGifCommands';
import * as FavoriteMemeCommands from '@app/features/expressions/commands/FavoriteMemeCommands';
import * as GifCommands from '@app/features/expressions/commands/GifCommands';
import FavoriteGif from '@app/features/expressions/state/FavoriteGif';
import FavoriteMemes from '@app/features/expressions/state/FavoriteMemes';
import * as FavoriteMemeUtils from '@app/features/expressions/utils/FavoriteMemeUtils';
import * as GifSlugUtils from '@app/features/expressions/utils/GifSlugUtils';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {modal, push} from '@app/features/ui/commands/ModalCommands';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {useLingui} from '@lingui/react/macro';
import {StarIcon, TrendUpIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useRef, useState} from 'react';

const VIDEO_FILE_EXTENSION_REGEX = /\.(mp4|webm|mov|m4v)(?:$|\?)/iu;
function isVideoSourceUrl(value: string): boolean {
	try {
		const url = new URL(value);
		return VIDEO_FILE_EXTENSION_REGEX.test(url.pathname);
	} catch {
		return VIDEO_FILE_EXTENSION_REGEX.test(value);
	}
}
export const GifPickerGridItem = observer(function GifPickerGridItem({
	item,
	coords,
	onClose,
	autoSendKlipyGifs,
	gifAutoPlay,
	searchTerm,
	onShowFavorites,
	onShowTrending,
	onSearchCategory,
	isFocused = false,
	itemKey,
}: {
	item: GifPickerGridItemData;
	coords: {
		position: 'absolute' | 'sticky';
		left?: number;
		right?: number;
		width: number;
		top?: number;
		height: number;
	};
	onClose?: () => void;
	autoSendKlipyGifs: boolean;
	gifAutoPlay: boolean;
	searchTerm: string;
	onShowFavorites: () => void;
	onShowTrending: () => void;
	onSearchCategory: (term: string) => void;
	isFocused?: boolean;
	itemKey?: string;
}) {
	const {t, i18n} = useLingui();
	const [isFavoritePending, setIsFavoritePending] = useState(false);
	const videoPool = useGifVideoPool();
	const videoContainerRef = useRef<HTMLDivElement>(null);
	const isSkeleton = item.type === 'skeleton';
	const proxySrc = (() => {
		if (item.type === 'gif') return item.gif.proxy_src;
		if (item.type === 'category') return item.previewProxySrc;
		return null;
	})();
	const mediaSourceUrl = (() => {
		if (item.type === 'gif') return item.gif.src;
		if (item.type === 'category') return item.previewUrl;
		return null;
	})();
	const usesVideoElement = !isSkeleton && mediaSourceUrl !== null && isVideoSourceUrl(mediaSourceUrl);
	const videoRef = usePooledVideo({
		src: usesVideoElement ? proxySrc : null,
		containerRef: videoContainerRef,
		videoPool,
		autoPlay: gifAutoPlay,
		enabled: usesVideoElement,
	});
	const playOnHover = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (event.pointerType !== 'mouse') return;
			const v = videoRef.current;
			if (!v) return;
			void safePlay(v);
		},
		[videoRef],
	);
	const stopOnHoverEnd = useCallback(
		(event: React.PointerEvent<HTMLDivElement>) => {
			if (event.pointerType !== 'mouse') return;
			const v = videoRef.current;
			if (!v) return;
			safePause(v);
		},
		[videoRef],
	);
	const handleClick = useCallback(
		(event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
			if (isSkeleton) return;
			if (item.type === 'category') {
				if (item.id === 'favorites') {
					onShowFavorites();
				} else if (item.id === 'trending') {
					onShowTrending();
				} else {
					onSearchCategory(item.id);
				}
				return;
			}
			const gif = item.gif;
			const provider = RuntimeConfig.gifProvider;
			const shiftKey = 'shiftKey' in event ? event.shiftKey : false;
			if (gif.favoriteGifLookup) {
				ComponentDispatch.dispatch('GIF_SELECT', {
					gif,
					autoSend: autoSendKlipyGifs && !shiftKey,
				});
				if (!shiftKey) onClose?.();
				return;
			}
			const shareId = GifSlugUtils.resolveShareId(provider, gif);
			if (!shareId) return;
			GifCommands.registerShare(shareId, searchTerm);
			const shareUrl = GifSlugUtils.resolveShareUrl(provider, {url: gif.url, slug: shareId});
			ComponentDispatch.dispatch('GIF_SELECT', {
				gif: {
					...gif,
					id: shareId,
					url: shareUrl,
				},
				autoSend: autoSendKlipyGifs && !shiftKey,
			});
			if (!shiftKey) onClose?.();
		},
		[autoSendKlipyGifs, isSkeleton, item, onClose, onSearchCategory, onShowFavorites, onShowTrending, searchTerm],
	);
	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key !== 'Enter' && event.key !== ' ') return;
			event.preventDefault();
			handleClick(event);
		},
		[handleClick],
	);
	const hoverPlaybackHandlers = gifAutoPlay
		? null
		: {
				onPointerEnter: playOnHover,
				onPointerLeave: stopOnHoverEnd,
			};
	if (isSkeleton) {
		return (
			<div
				className={clsx(styles.gridItem, styles.skeletonItem, isFocused && styles.gridItemFocused)}
				style={coords}
				data-grid-item={itemKey}
			>
				<div className={styles.gifMediaContainer}>
					<div className={styles.gifVideoContainer} />
				</div>
			</div>
		);
	}
	if (item.type === 'category') {
		let icon: React.ReactNode = null;
		let categoryClassName = styles.gridItemCategory;
		if (item.id === 'favorites') {
			icon = <StarIcon className={styles.gridItemIcon} />;
			categoryClassName = clsx(styles.gridItemCategory, styles.gridItemFavorites);
		} else if (item.id === 'trending') {
			icon = <TrendUpIcon className={styles.gridItemIcon} />;
		}
		const hasPreview = Boolean(proxySrc) && Boolean(mediaSourceUrl);
		return (
			<div
				role="button"
				tabIndex={0}
				className={clsx(
					styles.gridItem,
					categoryClassName,
					!hasPreview && styles.gridItemCategoryEmpty,
					isFocused && styles.gridItemFocused,
				)}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				style={coords}
				data-grid-item={itemKey}
				aria-label={t`Open ${item.title}`}
				{...(hoverPlaybackHandlers ?? {})}
			>
				{hasPreview && (
					<div className={styles.gifMediaContainer}>
						{usesVideoElement ? (
							<div ref={videoContainerRef} className={styles.gifVideoContainer} />
						) : (
							<img className={styles.gif} src={proxySrc ?? ''} alt="" loading="lazy" />
						)}
					</div>
				)}
				<div className={styles.gridItemBackdrop} />
				<div className={styles.gridItemCategoryTitle}>
					{icon}
					<div className={styles.gridItemCategoryTitleText}>{item.title}</div>
				</div>
			</div>
		);
	}
	const gif = item.gif;
	const provider = RuntimeConfig.gifProvider;
	const useSavedMedia = FavoriteGif.saveGifFavoritesAsSavedMedia;
	const persistedGifSlug = GifSlugUtils.resolveShareId(provider, gif);
	const favoriteMemes = FavoriteMemes.memes;
	const favoriteGifUrl = gif.favoriteGifLookup?.url ?? gif.url;
	const isFavorited = useSavedMedia
		? FavoriteMemeUtils.isFavoritedByGifSlug(favoriteMemes, provider, persistedGifSlug) ||
			(persistedGifSlug !== gif.id && FavoriteMemeUtils.isFavoritedByGifSlug(favoriteMemes, provider, gif.id))
		: FavoriteGif.hasUrl(favoriteGifUrl);
	const performFavoriteToggle = async () => {
		const useSavedMediaNow = FavoriteGif.saveGifFavoritesAsSavedMedia;
		if (!useSavedMediaNow) {
			if (FavoriteGif.hasUrl(favoriteGifUrl)) {
				FavoriteGifCommands.removeFavoriteGifByUrl(i18n, favoriteGifUrl);
			} else {
				const bestPreview = pickBestPreviewFormat(gif.media);
				FavoriteGifCommands.addFavoriteGif(i18n, {
					url: favoriteGifUrl,
					proxy_url: bestPreview?.format.proxy_src ?? gif.proxy_src,
					width: bestPreview?.format.width ?? gif.width,
					height: bestPreview?.format.height ?? gif.height,
					media: gif.media ?? {},
					content_type: bestPreview ? inferFormatContentType(bestPreview.key) : '',
				});
			}
			return;
		}
		setIsFavoritePending(true);
		try {
			if (isFavorited) {
				const meme =
					FavoriteMemeUtils.findFavoritedMeme(favoriteMemes, {
						gifProvider: provider,
						gifSlug: persistedGifSlug,
					}) ??
					(persistedGifSlug !== gif.id
						? FavoriteMemeUtils.findFavoritedMeme(favoriteMemes, {gifProvider: provider, gifSlug: gif.id})
						: null);
				if (meme) {
					await FavoriteMemeCommands.deleteFavoriteMeme(i18n, meme.id);
				}
			} else {
				const defaultName = FavoriteMemeUtils.deriveDefaultNameFromEmbedMedia(i18n, {
					url: gif.url,
					proxy_url: gif.proxy_src,
					flags: 0,
				});
				await FavoriteMemeCommands.createFavoriteMemeFromUrl(i18n, {
					url: gif.proxy_src,
					name: defaultName || gif.title,
					gifSlug: persistedGifSlug ?? undefined,
					gifProvider: persistedGifSlug ? provider : undefined,
					media: gif.media,
				});
			}
		} finally {
			setIsFavoritePending(false);
		}
	};
	const handleFavoriteClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isFavoritePending) return;
		if (!FavoriteGif.hasSeenFavoriteGifFirstTimePrompt && !isFavorited) {
			push(modal(() => <FavoriteGifFirstTimePromptModal onConfirm={() => void performFavoriteToggle()} />));
			return;
		}
		void performFavoriteToggle();
	};
	const favoriteTooltipText = (() => {
		if (isFavoritePending) return t`Updating favorites…`;
		if (isFavorited) return t`Remove from Favorites`;
		return t`Add to Favorites`;
	})();
	return (
		<FocusRing offset={-2}>
			<div
				role="button"
				tabIndex={0}
				className={clsx(
					styles.gridItem,
					styles.gridItemGif,
					styles.gridItemGifPicker,
					isFocused && styles.gridItemFocused,
					isFavoritePending && styles.gridItemFavoritePending,
				)}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				style={coords}
				data-grid-item={itemKey}
				aria-label={t`Send GIF ${gif.title}`}
				{...(hoverPlaybackHandlers ?? {})}
			>
				<div className={styles.gifMediaContainer}>
					{usesVideoElement ? (
						<div ref={videoContainerRef} className={styles.gifVideoContainer} />
					) : (
						<img className={styles.gif} src={proxySrc ?? ''} alt="" loading="lazy" />
					)}
				</div>
				<div className={styles.gridItemBackdrop} />
				<div className={styles.hoverActionButtons}>
					<Tooltip text={favoriteTooltipText} position="top">
						<FocusRing offset={-2}>
							<button
								type="button"
								onMouseDown={(e) => e.preventDefault()}
								onClick={handleFavoriteClick}
								className={clsx(styles.favoriteButton, isFavorited && styles.favoriteButtonActive)}
								aria-label={isFavorited ? t`Remove from Favorites` : t`Add to Favorites`}
								aria-busy={isFavoritePending}
								aria-pressed={isFavorited}
								disabled={isFavoritePending}
							>
								{isFavoritePending ? (
									<span className={styles.favoriteButtonSpinner} aria-hidden="true" />
								) : (
									<StarIcon
										size={18}
										weight={isFavorited ? 'fill' : 'bold'}
										className={isFavorited ? styles.favoriteButtonActiveIcon : styles.favoriteButtonIcon}
									/>
								)}
							</button>
						</FocusRing>
					</Tooltip>
				</div>
			</div>
		</FocusRing>
	);
});
